import { createFileRoute } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";

import { ReservedScheduleConfirm } from "@/components/reserved-schedules/reserved-schedule-confirm";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import {
  reservedScheduleFormAtom,
  reservedScheduleSlotFormAtom,
} from "@/lib/atoms/reserved-schedule-form-atom";
import { ReservedScheduleTypeEnum } from "@/lib/enums/reserved-schedule-type";
import { requireChosenModality } from "@/lib/reserved-schedules/require-chosen-modality";

import type { JSX } from "react";

const GetReservedSchedulePriceQuery = graphql(/* GraphQL */ `
  query GetReservedSchedulePriceQuery(
    $codModalidadePeriodoGarantido: Int!
    $codModalidadeMaisBarata: Int!
    $compararPrecos: Boolean!
    $codTipoSala: Int!
    $codUnidade: Int!
    $codUsuario: Int!
    $duracao: String!
  ) {
    ...GetReservedSchedulePriceFragment
    ...ReservedScheduleModalitiesFragment
  }
`);

const ReservedScheduleConfirmPage = (): JSX.Element => {
  const { user } = Route.useRouteContext();
  const reservedScheduleAtom = useAtomValue(reservedScheduleFormAtom);
  const reservedScheduleSlotAtom = useAtomValue(reservedScheduleSlotFormAtom);

  const cheaperModality = reservedScheduleSlotAtom.cheaperModality;
  // Só a convencional tem o que comparar — é o portão da tela, e não vale pedir ao backend um preço
  // que ela não vai mostrar.
  const comparesPrices =
    reservedScheduleAtom.modality === ReservedScheduleTypeEnum.Livance.valueOf() &&
    cheaperModality !== null;

  const queryResult = useGraphQL(GetReservedSchedulePriceQuery, {
    codModalidadePeriodoGarantido: reservedScheduleAtom.modality,
    // Com `compararPrecos: false` o segundo alias não é consultado, então o valor é irrelevante — mas
    // a variável é `Int!` e precisa de algo válido. A escolhida serve, e não mente: nesse caminho não
    // existe categoria mais barata.
    codModalidadeMaisBarata: cheaperModality ?? reservedScheduleAtom.modality,
    compararPrecos: comparesPrices,
    codTipoSala: reservedScheduleAtom.roomType,
    codUnidade: reservedScheduleAtom.location,
    codUsuario: user.codUsuario,
    duracao: reservedScheduleAtom.duration,
  });

  const { data } = queryResult;
  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Novo período garantido" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <ReservedScheduleConfirm
              reservedSchedulePrice={data}
              modalitiesFragment={data}
              reservedScheduleType={reservedScheduleAtom.modality}
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

const reservedScheduleSearchSchema = z.object({
  action: z.string().optional(),
  // Código estável do erro de criação (`PG_*`), quando o backend mandou um. Ausente ⇒ drawer genérico.
  errorCode: z.string().optional(),
  // Unidade e datas (`yyyy-MM-dd`, separadas por vírgula) do Período Garantido que o membro já tem
  // no horário, quando o erro é a sobreposição (`PG_SOBREPOSICAO`). Ausentes ⇒ o texto não as cita.
  overlapUnit: z.string().optional().catch(undefined),
  overlapDates: z.string().optional().catch(undefined),
  // `motivoAgendaNaoCriada` da API quando o PG foi criado sem a agenda pedida (KRA-460). Ausente ⇒
  // aviso genérico.
  linkedScheduleReason: z.string().optional(),
});

export const Route = createFileRoute("/settings/reserved-schedules/create/confirm")({
  component: ReservedScheduleConfirmPage,
  loader: requireChosenModality,
  validateSearch: reservedScheduleSearchSchema,
});
